/**
 * KPICard — single tile in the Overview KPI row (IA §2.2.1).
 *
 * One big value, a label, an optional sub-label, and a
 * `<PeriodCompareDelta>` pill for period-over-period context. Three extrinsic
 * states are supported:
 *
 *   - loading  → skeleton blocks in place of value + delta, and the sublabel
 *                row held open but empty (see `everHadSubLabel`)
 *   - empty    → value === null renders "No data" muted text
 *   - error    → rendered by the parent tab-level error boundary; this
 *                component intentionally does not try to display errors
 *                (they need a retry affordance that the boundary owns).
 *
 * Formatting is a prop, not coupled to the value type — callers pass
 * `'number' | 'rating' | 'percent' | 'hours' | 'currency'` and we dispatch
 * to `Intl.NumberFormat` / `.toFixed(1)` accordingly. `'percent'` formats
 * the raw number as `NN.N%` — use it for retention / thumbs-up / delivery
 * rate etc. `'rating'` (★ suffix) is reserved for 0-5 scales.
 *
 * ### Info-icon (Overview P2-3 / Engagement P2-04 / Operations P3-6 / Quality F5)
 *
 * When `tooltip` is provided, a real `<button>` with `aria-label` is rendered
 * next to the label text. Keyboard-focusable (Tab + Enter to reveal), the
 * visible "i" glyph is `aria-hidden` so screen readers hear the aria-label
 * instead of "DAUi", and a transparent `before:` pseudo-element inflates the
 * hit-target to ~40×40 px without stealing layout space (the visible glyph
 * stays 16×16). Swapped from Radix `Tooltip` to `Popover` so tap opens the
 * rich copy on touch devices — the old hover/focus-only tooltip never fired
 * on iOS Safari tap (Overview P2-3). The label text itself is exposed via
 * `data-testid="kpi-label"` so snapshots can assert on the label element
 * specifically (its `textContent` is just `{label}`, with no trailing "i"
 * glyph bleed).
 *
 * ### TODO(audit): consumers supplying raw current/previous to the delta
 * pill (OverviewPage, BriefsPage, OperationsPage, QualityPage) should pass
 * `deltaCurrent` / `deltaPrevious` here so the PeriodCompareDelta tooltip
 * surfaces the absolute scale (e.g. "-100%" on Operations). A `sampleSize`
 * prop is also forwarded for small-N muting (Quality P2-4). Page agents
 * should adopt these in the next round.
 */

import * as React from 'react';

import { Card, CardContent } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { PeriodCompareDelta } from './PeriodCompareDelta';
import { formatRating } from '@/lib/format';
import { cn } from '@/lib/utils';

export type KpiFormat = 'number' | 'rating' | 'percent' | 'hours' | 'currency';

export interface KPICardProps {
  label: string;
  value: string | number | null;
  format?: KpiFormat;
  /** Ratio of change vs prev period; forwarded to <PeriodCompareDelta>. */
  delta?: number | null;
  /** If true, positive delta is bad (cost, errors). */
  inverted?: boolean;
  /** Show the period-over-period delta pill. Default true. Set false on tiles
   *  that have no period comparison by design (e.g. the Jira catalog KPIs) so
   *  the slot doesn't render a meaningless "—" pill that reads as missing data.
   *  Within one KPI row either all or none opt out, so layout heights stay even. */
  showDelta?: boolean;
  loading?: boolean;
  /** Secondary one-liner under the main value (e.g. "87 (12 w/ text)"). */
  subLabel?: string;
  /** Optional extra class to tune width/grid placement from caller. */
  className?: string;
  /** Optional hover-tooltip explaining what this KPI actually measures.
   *  Rendered as a small info icon next to the label. */
  tooltip?: string;
  /** Raw current-period value forwarded to the delta pill tooltip. */
  deltaCurrent?: number | null;
  /** Raw previous-period value forwarded to the delta pill tooltip. */
  deltaPrevious?: number | null;
  /** Sample size of the current period — forwarded to the delta pill so
   *  small-N deltas (n<3) are muted per Quality P2-4. */
  sampleSize?: number;
  /** Minimum previous-period sample before a numeric delta is shown.
   *  Forwarded verbatim to PeriodCompareDelta. Default 10 for counts, 0 for
   *  ratio-like formats (percent / rating / hours) — without the
   *  format-aware override the default-10 threshold misfires on rates
   *  because the raw previous value is <10 by construction (0..1 ratio,
   *  0..5 rating, single-digit hours), so every rate KPI rendered the
   *  "new" pill regardless of timeframe. Explicit prop still wins. */
  minPreviousSampleForDelta?: number;
}

const NUMBER_FMT = new Intl.NumberFormat('en-US');
const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

/** Apply the requested display format. Preserves strings as-is for callers
 *  that want to render a pre-formatted value. */
function formatValue(value: string | number, fmt: KpiFormat): string {
  if (typeof value === 'string') return value;
  switch (fmt) {
    case 'rating':
      return formatRating(value);
    case 'percent':
      // 72 → "72.0%". Caller is responsible for scaling 0..1 ratios to
      // 0..100 (see EngagementPage.retentionCurrent). Using ★ here was the
      // bug-3 source — the star glyph (U+2605) was visually mistaken for
      // an asterisk ("72%*").
      return `${value.toFixed(1)}%`;
    case 'hours':
      return `${value.toFixed(1)}h`;
    case 'currency':
      return CURRENCY_FMT.format(value);
    case 'number':
    default:
      return NUMBER_FMT.format(value);
  }
}

export function KPICard({
  label,
  value,
  format = 'number',
  delta,
  inverted,
  showDelta = true,
  loading = false,
  subLabel,
  className,
  tooltip,
  deltaCurrent,
  deltaPrevious,
  sampleSize,
  minPreviousSampleForDelta,
}: KPICardProps): React.ReactElement {
  // Remembers whether this tile has ever carried a sublabel, so its row can be
  // held open while the payload that produces it is away. Written during
  // render on purpose: it is a cache of this tile's own output, it only ever
  // goes false -> true, and it feeds nothing outside this component.
  const everHadSubLabel = React.useRef(false);
  if (subLabel) everHadSubLabel.current = true;

  // Render tree is shared across states so layout heights stay stable —
  // prevents layout shift when a slow KPI finishes loading.
  return (
    <Card data-testid="kpi-card" className={cn('', className)}>
      <CardContent className="p-3 space-y-1">
        <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
          {/*
            kpi-label: isolated node so `textContent` on it reads the label
            text alone. The info-icon button sits as a sibling, and its glyph
            is aria-hidden so screen readers rely on the button's aria-label
            instead of hearing "DAUi…".
          */}
          <span data-testid="kpi-label">{label}</span>
          {tooltip && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={`About ${label}`}
                  data-testid="kpi-info"
                  // Visual glyph stays 16x16 (h-4 w-4 + shrink-0 guards the
                  // row layout). Hit target is inflated to ~40x40 via a
                  // transparent `before:` pseudo-element so touch users
                  // (Overview P2-3 / Engagement P2-04 / Quality F5) can
                  // reliably tap; `-m-2` negative-margin was dropped because
                  // it overlapped the label text under `gap-1`.
                  // Swapped Tooltip → Popover so tap opens the rich copy on
                  // touch devices — tooltip was hover/focus-only and never
                  // fired on iOS Safari tap.
                  className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-muted-foreground/40 text-[10px] leading-none text-muted-foreground/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary before:absolute before:inset-[-12px] before:content-['']"
                >
                  {/* Visible glyph — aria-hidden so AT reads the button's
                      aria-label only. Wrapping the glyph (instead of the
                      button) keeps Radix' own ARIA attributes intact. */}
                  <span aria-hidden="true">i</span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                sideOffset={6}
                className="w-auto max-w-[240px] p-2 text-xs text-left leading-snug"
              >
                {tooltip}
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* One fixed-height row for all three states. The tile is blanked on
            every filter change now (a value from the previous filters is a
            wrong answer, not a stale one), so a skeleton that is not exactly
            as tall as the value it stands in for moves the page under the
            reader's cursor on every click. */}
        <div className="h-7 flex items-center">
          {loading ? (
            <div
              data-testid="kpi-skeleton"
              className="h-6 w-24 rounded bg-muted animate-pulse"
            />
          ) : value === null ? (
            <div
              data-testid="kpi-empty"
              className="text-sm text-muted-foreground italic"
            >
              No data
            </div>
          ) : (
            <div
              data-testid="kpi-value"
              className="text-xl font-semibold leading-tight"
            >
              {formatValue(value, format)}
            </div>
          )}
        </div>

        {/* A sublabel is derived from the payload, so a caller CANNOT pass one
            while the payload is away — which is why the slot is reserved from
            what this tile has already shown rather than from the current
            props. A tile that has carried a sublabel keeps that row through
            the recount instead of collapsing it. */}
        {everHadSubLabel.current && (
          <div
            className="h-4 text-xs text-muted-foreground"
            data-testid="kpi-sublabel"
          >
            {!loading && value !== null ? subLabel : null}
          </div>
        )}

        {/* The slot reserves its vertical space in every state — while loading
            it holds a skeleton the size of the pill, because a pill that
            appears only once the numbers land makes each tile grow by its own
            height on every filter change. Passing null renders the em-dash.
            Tiles with no period comparison opt out via showDelta=false to
            avoid a "—" pill that reads as missing data. */}
        {showDelta && (
          <div className="h-6 flex items-center">
            {loading ? (
              <div
                data-testid="kpi-delta-skeleton"
                className="h-4 w-12 rounded bg-muted animate-pulse"
              />
            ) : (
            <PeriodCompareDelta
              delta={delta ?? null}
              inverted={inverted}
              current={deltaCurrent}
              previous={deltaPrevious}
              sampleSize={sampleSize}
              // Thread the KPI's own label into the pill tooltip so the hover
              // text reads "Time saved: 2.8h (was 1.5h)" instead of the
              // generic "vs prev period: 2.80 (was 1.50)" — readers already
              // know which KPI they're hovering.
              label={label}
              // Format-aware default so rate / rating / hours KPIs don't
              // render "new" on every timeframe. Explicit prop wins.
              minPreviousSampleForDelta={
                minPreviousSampleForDelta ??
                (format === 'percent' || format === 'rating' || format === 'hours'
                  ? 0
                  : undefined)
              }
              // Same format the tile uses for its visible value — keeps the
              // pill tooltip's unit + precision in lock-step with the
              // headline, so no "2.8h" up top and "2.80" in the tooltip.
              format={format}
            />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
